import { AUTH_API_ENDPOINT } from "../../../constants/constant";
import baseApi from "../../baseApi";
import {
  ApiResponse,
  Log,
  OtpPayload,
  Refferal,
  ResetPasswordPayload,
  ResultType,
  SignInPayload,
  UseQueryParams,
  UserData,
} from "./auth.type";

interface useParams {
  search?: string;
  status?: string;
  userType?: string;
  page?: number;
  limit?: number;
}

export const authApiSlice = baseApi.injectEndpoints({
  endpoints: (build) => ({
    signInApiCall: build.mutation<any, SignInPayload>({
      query: (signInBody) => ({
        url: AUTH_API_ENDPOINT.SIGNIN,
        method: "POST",
        body: signInBody,
      }),
    }),

    verifyOtpApiCall: build.mutation<any, OtpPayload>({
      query: (verifyOtpBody) => ({
        url: AUTH_API_ENDPOINT.VERIFY_OTP,
        method: "PATCH",
        body: verifyOtpBody,
      }),
    }),

    resendOtpApiCall: build.mutation<any, Omit<OtpPayload, "otp">>({
      query: (resendOtpBody) => ({
        url: AUTH_API_ENDPOINT.RESEND_OTP,
        method: "POST",
        body: resendOtpBody,
      }),
    }),

    forgotPasswordApiCall: build.mutation<any, { email: string }>({
      query: (forgotPasswordBody) => ({
        url: AUTH_API_ENDPOINT.FORGOT_PASSWORD,
        method: "POST",
        body: forgotPasswordBody,
      }),
    }),

    resetPasswordApiCall: build.mutation<any, ResetPasswordPayload>({
      query: (forgotPasswordBody) => ({
        url: AUTH_API_ENDPOINT.RESET_PASSWORD,
        method: "POST",
        body: forgotPasswordBody,
      }),
    }),

    getProfileApiCall: build.query<ApiResponse<UserData>, void>({
      query: () => ({
        url: AUTH_API_ENDPOINT.GET_PROFILE,
        method: "GET",
      }),
      providesTags: ["Profile"],
    }),

    updateProfileApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: AUTH_API_ENDPOINT.UPDATE_PROFILE,
        method: "PUT",
        body: profileBody,
      }),
      invalidatesTags: ["Profile"],
    }),

    getSubAdminApiCall: build.query<
      ApiResponse<ResultType<UserData>>,
      useParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        if (params?.userType) queryParams.append("userType", params.userType);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());

        return `${AUTH_API_ENDPOINT.GET_SUB_ADMIN}?${queryParams.toString()}`;
      },
      providesTags: ["SubAdmin"],
    }),

    createSubAdminApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: AUTH_API_ENDPOINT.CREATE_SUB_ADMIN,
        method: "POST",
        body: profileBody,
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    deleteSubAdminApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.DELETE_SUB_ADMIN}?userId=${profileBody.userId}`,
        method: "DELETE",
        body: profileBody,
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    changeSubAdminStatusApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.CHANGE_SUB_ADMIN_STATUS}?userId=${profileBody.userId}&reason=${profileBody.reason}`,
        method: "PUT",
        body: profileBody,
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    getNotificationActivityApiCall: build.query<
      ApiResponse<ResultType<Log>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());

        return `${
          AUTH_API_ENDPOINT.GET_NOTIFICATIONS_ACTIVITY
        }?${queryParams.toString()}`;
      },
    }),

    addReferralApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.ADD_REFERRAL}`,
        method: "POST",
        body: profileBody,
      }),
      invalidatesTags: ["Referral_Code"],
    }),

    addUsersReferralApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.ADD_REFERRAL_USER}`,
        method: "POST",
        body: profileBody,
      }),
      invalidatesTags: ["Users_Referral_Code"],
    }),

    getRefferalsApiCall: build.query<
      ApiResponse<ResultType<Refferal>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.userId) queryParams.append("userId", params.userId);
        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        if (params?.referrerCode)
          queryParams.append("referrerCode", params.referrerCode);
        return `${AUTH_API_ENDPOINT.GET_REFFERAL}?${queryParams.toString()}`;
      },
      providesTags: ["Referral_Code"],
    }),

    getRefferalsCodeApiCall: build.query<
      ApiResponse<ResultType<Refferal>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.userId)
          queryParams.append("userId", params.userId.toString());
        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        return `${
          AUTH_API_ENDPOINT.GET_REFFERAL_CODE
        }?${queryParams.toString()}`;
      },
      providesTags: ["Referral_Code"],
    }),

    getUsersRefferalsCodeApiCall: build.query<
      ApiResponse<ResultType<Refferal>>,
      UseQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.userId)
          queryParams.append("userId", params.userId.toString());
        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        return `${
          AUTH_API_ENDPOINT.GET_REFFERAL_USER
        }?${queryParams.toString()}`;
      },
      providesTags: ["Users_Referral_Code"],
    }),

    changeStatusReferralApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.CHANGE_STATUS_REFFERAL}?_id=${profileBody._id}`,
        method: "PUT",
        body: profileBody,
      }),
      invalidatesTags: ["Referral_Code"],
    }),

    changeStatusUsersReferralApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.CHANGE_STATUS_REFFERAL_USER}?_id=${profileBody._id}`,
        method: "PUT",
        body: profileBody,
      }),
      invalidatesTags: ["Users_Referral_Code"],
    }),

    deleteReferralApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.DELETE_REFFERAL}?_id=${profileBody._id}`,
        method: "DELETE",
        body: profileBody,
      }),
      invalidatesTags: ["Referral_Code"],
    }),

    deleteReferralUsersApiCall: build.mutation<ApiResponse, any>({
      query: (profileBody) => ({
        url: `${AUTH_API_ENDPOINT.DELETE_REFFERAL_USER}?_id=${profileBody._id}`,
        method: "DELETE",
        body: profileBody,
      }),
      invalidatesTags: ["Users_Referral_Code"],
    }),
  }),
});

export const {
  useSignInApiCallMutation,
  useGetProfileApiCallQuery,
  useResendOtpApiCallMutation,
  useVerifyOtpApiCallMutation,
  useGetSubAdminApiCallQuery,
  useDeleteSubAdminApiCallMutation,
  useCreateSubAdminApiCallMutation,
  useUpdateProfileApiCallMutation,
  useResetPasswordApiCallMutation,
  useForgotPasswordApiCallMutation,
  useChangeSubAdminStatusApiCallMutation,
  useGetNotificationActivityApiCallQuery,
  useGetRefferalsApiCallQuery,
  useLazyGetRefferalsApiCallQuery,
  useAddReferralApiCallMutation,
  useAddUsersReferralApiCallMutation,
  useDeleteReferralApiCallMutation,
  useDeleteReferralUsersApiCallMutation,
  useChangeStatusReferralApiCallMutation,
  useChangeStatusUsersReferralApiCallMutation,
  useGetRefferalsCodeApiCallQuery,
  useGetUsersRefferalsCodeApiCallQuery,
} = authApiSlice;

export default authApiSlice;
